import { useEffect, useState } from "react";
import { api, euro, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Receipt } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "sueldo", label: "Sueldo" },
  { value: "alquiler", label: "Alquiler" },
  { value: "financiacion", label: "Financiación / plazos" },
  { value: "suscripcion", label: "Suscripción / software" },
  { value: "seguridad_social", label: "Seguridad Social" },
  { value: "autonomo", label: "Cuota autónomo" },
  { value: "luz", label: "Luz" },
  { value: "agua", label: "Agua" },
  { value: "telefono", label: "Teléfono" },
  { value: "internet", label: "Internet" },
  { value: "varios", label: "Varios" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const PERIOD = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "anual", label: "Anual" },
];

const empty = {
  center_id: "",
  concept: "",
  category: "varios",
  amount: 0,
  periodicity: "mensual",
  next_due_date: "",
  supplier_id: "",
  notes: "",
  active: true,
  payment_status: "pending",
};

const STATUS_META = {
  pending: { label: "Pendiente", className: "bg-[#F3E5D8] text-[#2A2F2C]" },
  paid: { label: "Pagada", className: "bg-[#D1E0D7] text-[#2A2F2C]" },
  returned: { label: "Devuelta", className: "bg-[#F4D8D2] text-[#A23F2D]" },
};

export default function RecurringExpenses() {
  const { centers } = useAuth();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [summary, setSummary] = useState({ monthly_total: 0, yearly_total: 0, count: 0 });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: list }, { data: sup }, { data: sum }] = await Promise.all([
        api.get("/recurring-expenses"),
        api.get("/suppliers").catch(() => ({ data: [] })),
        api.get("/recurring-expenses/summary"),
      ]);
      setItems(list);
      setSuppliers(sup);
      setSummary(sum);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...empty, center_id: centers[0]?.id || "" });
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (it) => {
    setForm({
      center_id: it.center_id || "",
      concept: it.concept,
      category: it.category || "varios",
      amount: it.amount || 0,
      periodicity: it.periodicity || "mensual",
      next_due_date: it.next_due_date || "",
      supplier_id: it.supplier_id || "",
      notes: it.notes || "",
      active: it.active !== false,
      payment_status: it.payment_status || "pending",
    });
    setEditingId(it.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount) || 0,
        center_id: form.center_id || null,
        supplier_id: form.supplier_id || null,
        next_due_date: form.next_due_date || null,
      };
      if (editingId) {
        await api.patch(`/recurring-expenses/${editingId}`, payload);
        toast.success("Gasto fijo actualizado");
      } else {
        await api.post("/recurring-expenses", payload);
        toast.success("Gasto fijo creado");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este gasto fijo?")) return;
    try {
      await api.delete(`/recurring-expenses/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateStatus = async (it, value) => {
    try {
      await api.patch(`/recurring-expenses/${it.id}/status`, { payment_status: value });
      toast.success("Estado actualizado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const statusOf = (it) => it.payment_status || "pending";

  const centerName = (id) => centers.find((c) => c.id === id)?.name || "Global";

  return (
    <div className="space-y-6" data-testid="recurring-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Administración
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Gastos fijos
          </h1>
          <p className="text-sm text-[#6E7772] mt-2 max-w-2xl">
            Sueldos, alquileres, plazos a proveedores, suscripciones… Lo que pagas
            periódicamente. Te ayuda a saber cuánto te cuesta el negocio cada mes.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-recurring-btn"
              onClick={openNew}
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nuevo gasto fijo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editingId ? "Editar gasto fijo" : "Nuevo gasto fijo"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Concepto *</Label>
                <Input
                  data-testid="recurring-concept"
                  required
                  placeholder="Ej. Alquiler local Madrid"
                  value={form.concept}
                  onChange={(e) => setForm({ ...form, concept: e.target.value })}
                />
              </div>
              <div>
                <Label>Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Centro</Label>
                <Select
                  value={form.center_id || "global"}
                  onValueChange={(v) => setForm({ ...form, center_id: v === "global" ? "" : v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (todos los centros)</SelectItem>
                    {centers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Importe (€) *</Label>
                <Input
                  data-testid="recurring-amount"
                  type="number"
                  required
                  step="0.01"
                  min={0}
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>
              <div>
                <Label>Periodicidad *</Label>
                <Select value={form.periodicity} onValueChange={(v) => setForm({ ...form, periodicity: v })}>
                  <SelectTrigger data-testid="recurring-periodicity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERIOD.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Próximo vencimiento</Label>
                <Input
                  type="date"
                  value={form.next_due_date}
                  onChange={(e) => setForm({ ...form, next_due_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Proveedor (opcional)</Label>
                <Select
                  value={form.supplier_id || "none"}
                  onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Notas</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div>
                <Label>Estado del pago</Label>
                <Select
                  value={form.payment_status}
                  onValueChange={(v) => setForm({ ...form, payment_status: v })}
                >
                  <SelectTrigger data-testid="recurring-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente de pagar</SelectItem>
                    <SelectItem value="paid">Pagada</SelectItem>
                    <SelectItem value="returned">Devuelta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="rec-active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                <label htmlFor="rec-active" className="text-sm">Activo</label>
              </div>
              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-recurring-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="recurring-summary">
        <Card className="border-[#E5E3DB]">
          <CardContent className="p-5">
            <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772]">Mensual</div>
            <div className="font-heading text-3xl font-medium text-[#2A2F2C] mt-2">{euro(summary.monthly_total)}</div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E3DB]">
          <CardContent className="p-5">
            <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772]">Anual</div>
            <div className="font-heading text-3xl font-medium text-[#2A2F2C] mt-2">{euro(summary.yearly_total)}</div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E3DB]">
          <CardContent className="p-5">
            <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772]">Conceptos activos</div>
            <div className="font-heading text-3xl font-medium text-[#2A2F2C] mt-2">{summary.count}</div>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg border border-[#E5E3DB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-[#E5E3DB]">
              <TableHead>Concepto</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Centro</TableHead>
              <TableHead className="text-right">Importe</TableHead>
              <TableHead>Periodicidad</TableHead>
              <TableHead>Próximo</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[#6E7772] py-12">
                  Aún no hay gastos fijos. Crea el primero para empezar a controlar costes.
                </TableCell>
              </TableRow>
            )}
            {items.map((it) => (
              <TableRow key={it.id} className="border-[#E5E3DB]" data-testid={`recurring-row-${it.id}`}>
                <TableCell>
                  <div className="font-medium">{it.concept}</div>
                  {!it.active && <Badge variant="outline" className="mt-1 text-[10px]">Inactivo</Badge>}
                </TableCell>
                <TableCell className="text-sm">{CAT_LABEL[it.category] || it.category}</TableCell>
                <TableCell className="text-sm">{centerName(it.center_id)}</TableCell>
                <TableCell className="text-right font-medium">{euro(it.amount || 0)}</TableCell>
                <TableCell className="text-sm">{it.periodicity}</TableCell>
                <TableCell className="text-sm">{it.next_due_date || "—"}</TableCell>
                <TableCell>
                  <Select
                    value={statusOf(it)}
                    onValueChange={(v) => updateStatus(it, v)}
                  >
                    <SelectTrigger
                      className={`h-8 w-36 border-0 ${STATUS_META[statusOf(it)].className}`}
                      data-testid={`recurring-status-${it.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="paid">Pagada</SelectItem>
                      <SelectItem value="returned">Devuelta</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-recurring-${it.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-[#C07F67]" onClick={() => remove(it.id)} data-testid={`del-recurring-${it.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
